import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES } from '../i18n'
import { isNativePlatform } from './platform'
import type { Locale } from './api/types'

// The document head was fixed Italian for every route, so a crawler saw an Italian title and no
// alternates on /fr, /zh and the rest — the translations existed but nothing advertised them.
//
// No new copy is written here: the title and description are composed from strings the glossary
// already approved (brand.tagline, home.heroSubtitle). Italian keeps the exact title it is already
// indexed under; the other languages simply had none of their own.
const PINNED_TITLE: Partial<Record<Locale, string>> = {
  it: 'Garage San Marco — Prenota il tuo parcheggio a Venezia',
}

function upsertMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`
  let el = document.head.querySelector<HTMLLinkElement>(sel)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (hreflang) el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Keeps <title>, the description and the hreflang alternates in step with the active language and
 * route. Alternates are built by swapping the locale segment of the CURRENT path, so a subpage
 * points at its own translations rather than at the seven home pages.
 */
export function useDocumentHead(locale: Locale, pathname: string) {
  const { t } = useTranslation()

  useEffect(() => {
    document.title = PINNED_TITLE[locale] ?? `Garage San Marco — ${t('brand.tagline')}`
    upsertMeta('description', t('home.heroSubtitle'))

    // Hash routing in the native shell puts the route outside the pathname, and the app inside a
    // WebView is not crawled — canonical/alternates would only be wrong there.
    if (isNativePlatform()) return

    const parts = pathname.split('/').filter(Boolean)
    const rest = SUPPORTED_LOCALES.includes(parts[0] as Locale) ? parts.slice(1) : parts
    const urlFor = (l: Locale) => `${window.location.origin}/${[l, ...rest].join('/')}`

    upsertLink('canonical', urlFor(locale))
    for (const l of SUPPORTED_LOCALES) upsertLink('alternate', urlFor(l), l)
    upsertLink('alternate', urlFor('it'), 'x-default')
  }, [locale, pathname, t])
}
